import { useState } from 'react';
import { Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAppDispatch } from '@/features/hooks';
import { createFromTransaction } from '@/features/templates/templatesSlice';
import { useToast } from '@/hooks/use-toast';
import type { Transaction } from '@/lib/types';

interface SaveAsTemplateButtonProps {
  transaction: Transaction;
  variant?: 'ghost' | 'outline' | 'default';
  size?: 'sm' | 'default';
  className?: string;
}

export function SaveAsTemplateButton({
  transaction,
  variant = 'outline',
  size = 'sm',
  className,
}: SaveAsTemplateButtonProps) {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const handleClick = async () => {
    setSaving(true);
    try {
      await dispatch(createFromTransaction(transaction.id)).unwrap();
      toast({
        title: 'Template created from transaction',
        description: `A new template was created from "${transaction.description}".`,
      });
    } catch (err) {
      toast({
        title: 'Failed to create template',
        description: typeof err === 'string' ? err : 'An unexpected error occurred.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={handleClick}
      disabled={saving}
      data-testid="save-as-template-btn"
    >
      <Layers className="h-4 w-4 mr-1.5" />
      {saving ? 'Saving...' : 'Save as Template'}
    </Button>
  );
}
